import { useState, useCallback, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { isSameDay } from '@/lib/date';
import { useTodayKey } from '@/hooks/useTodayKey';
import { buildCoveredDays, type OngoingEvent } from '@/lib/ongoing';

export interface MiniCalendarProps {
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
  onClose: () => void;
  /** Same shape as DateStrip's prop. When provided, renders up to 3 dots under each day number. */
  datesWithItems?: Set<string> | Map<string, string[]>;
  /**
   * Called whenever the viewed month changes via the prev/next chevrons.
   * Lets the parent prefetch data for that month so the dots are accurate.
   * Receives a Date pointing at the 1st of the new month.
   */
  onViewMonthChange?: (newMonth: Date) => void;
  /**
   * User's profile timezone. "Today"/"past" are resolved in this timezone
   * (matching the schedule feed) so a device clock in a different timezone
   * can't mis-classify a feed-past day as today/enabled.
   */
  userTimezone?: string | null;
  /**
   * Still-running multi-day runs, already ranked by `buildOngoingEvents`.
   *
   * Drives the period band under the numeral. Without it a run of any length
   * shows only its two endpoints here, so a four-month tuition period lights
   * 2 days out of ~120 and a month wholly inside it shows nothing at all
   * (YAR-418).
   */
  ongoingRuns?: OngoingEvent[];
}

const DAY_HEADERS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function getCalendarGrid(month: Date): (Date | null)[] {
  const year = month.getFullYear();
  const m = month.getMonth();
  const firstDay = new Date(year, m, 1).getDay();
  const daysInMonth = new Date(year, m + 1, 0).getDate();

  const grid: (Date | null)[] = [];
  for (let i = 0; i < firstDay; i++) grid.push(null);
  for (let d = 1; d <= daysInMonth; d++) grid.push(new Date(year, m, d));
  while (grid.length % 7 !== 0) grid.push(null);
  return grid;
}

function toDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Whether the day carries anything at all. Mirrors DateStrip's helper so the
 * two pickers keep the same visual contract — they read the same
 * `datesWithItems`, and letting them disagree is drift QA reports as a bug in
 * whichever one they open second. The Map form's per-item colours no longer
 * matter: the cell says "something is here", not how many or whose (YAR-420).
 */
function hasItemsOn(
  datesWithItems: Set<string> | Map<string, string[]> | undefined,
  dateKey: string,
): boolean {
  if (!datesWithItems) return false;
  return datesWithItems.has(dateKey);
}

export function MiniCalendar({
  selectedDate,
  onDateSelect,
  onClose,
  datesWithItems,
  onViewMonthChange,
  userTimezone,
  ongoingRuns,
}: MiniCalendarProps) {
  const [viewMonth, setViewMonth] = useState(
    () => new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1),
  );
  // The feed keeps scrolling behind the open calendar — the backdrop does
  // not stop touch scroll — and every day it passes becomes `selectedDate`.
  // Follow it: a calendar that stays on the month it opened on while the
  // feed is a month further reads as broken (YAR-420 re-open). A month
  // paged to with the chevrons is kept until the feed actually moves.
  useEffect(() => {
    setViewMonth((prev) => {
      const y = selectedDate.getFullYear();
      const m = selectedDate.getMonth();
      return prev.getFullYear() === y && prev.getMonth() === m
        ? prev
        : new Date(y, m, 1);
    });
  }, [selectedDate]);

  // Resolve "today" in the user's profile timezone (the same source the
  // schedule feed uses) rather than the device clock, so the calendar and the
  // feed always agree on which days are past. useTodayKey re-renders at the
  // timezone's midnight, so a calendar left open across the day boundary
  // can't keep yesterday's cell enabled.
  const todayKey = useTodayKey(userTimezone);

  const grid = getCalendarGrid(viewMonth);

  // Keyed by day so the band can ask about a neighbour without re-deriving it,
  // and so a run spanning the whole month costs one pass rather than one per
  // cell.
  const gridKeys = useMemo(
    () => grid.map((d) => (d ? toDateKey(d) : null)),
    [grid],
  );
  const coveredDays = useMemo(
    () => buildCoveredDays(ongoingRuns ?? [], gridKeys),
    [ongoingRuns, gridKeys],
  );

  const goToPrevMonth = useCallback(() => {
    setViewMonth((prev) => {
      const next = new Date(prev.getFullYear(), prev.getMonth() - 1, 1);
      onViewMonthChange?.(next);
      return next;
    });
  }, [onViewMonthChange]);

  const goToNextMonth = useCallback(() => {
    setViewMonth((prev) => {
      const next = new Date(prev.getFullYear(), prev.getMonth() + 1, 1);
      onViewMonthChange?.(next);
      return next;
    });
  }, [onViewMonthChange]);

  const handleDateClick = useCallback(
    (date: Date) => {
      onDateSelect(date);
      onClose();
    },
    [onDateSelect, onClose],
  );

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[199]"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Calendar dropdown — Figma `.Calendar` (10369:140207): a 264px card
          left-aligned under the month label, 12px padding, 12px between the
          navigation row and the grid, `border/default` + the popover shadow.
          A fixed width on purpose: it used to stretch to its container, so
          it opened at one width from the hero and a wider one from the
          sticky header (YAR-420 re-open). */}
      <div className="absolute left-0 z-[200] mt-1 flex w-66 flex-col gap-3 rounded-lg border border-border-strong bg-background p-3 shadow-card">
        {/* Navigation: month label, then the two 28px chevron buttons. */}
        <div className="flex items-center justify-between">
          <span className="text-base font-semibold text-muted-foreground">
            {MONTH_NAMES[viewMonth.getMonth()]} {viewMonth.getFullYear()}
          </span>
          <div className="flex items-center">
            <button
              type="button"
              onClick={goToPrevMonth}
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:text-foreground cursor-pointer"
              aria-label="Previous month"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              type="button"
              onClick={goToNextMonth}
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:text-foreground cursor-pointer"
              aria-label="Next month"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {/* 24px cells on a 36px pitch (12px gaps), rows 12px apart — the
            `Week` / `Week Row` geometry of the Figma component. */}
        <div className="flex flex-col gap-3">
          {/* Day headers */}
          <div className="grid grid-cols-7 gap-3">
            {DAY_HEADERS.map((d, i) => (
              <div
                key={i}
                className="flex h-6 w-6 items-center justify-center text-sm text-text-hero"
              >
                {d}
              </div>
            ))}
          </div>

          {/* Date cells */}
          <div className="grid grid-cols-7 gap-3">
            {grid.map((date, i) => {
              if (!date) {
                // Empty leading/trailing cell: same footprint as a day.
                return (
                  <div
                    key={i}
                    className="h-6 w-6"
                  />
                );
              }

              const dateKey = toDateKey(date);
              const isPast = dateKey < todayKey;
              const isToday = dateKey === todayKey;
              const isSelected = isSameDay(date, selectedDate);
              const hasItems = hasItemsOn(datesWithItems, dateKey);

              // Row-aware neighbours: column 0 never joins left and column 6
              // never joins right, because the calendar-adjacent day is on
              // another line and a band that reached the edge would read as
              // continuing into it.
              const isCovered = coveredDays.has(dateKey);
              const col = i % 7;
              const prevKey = col > 0 ? gridKeys[i - 1] : null;
              const nextKey = col < 6 ? gridKeys[i + 1] : null;
              const joinsLeft =
                isCovered && !!prevKey && coveredDays.has(prevKey);
              const joinsRight =
                isCovered && !!nextKey && coveredDays.has(nextKey);

              return (
                <div
                  key={i}
                  className="relative h-6 w-6"
                >
                  <button
                    type="button"
                    disabled={isPast}
                    onClick={() => handleDateClick(date)}
                    className={[
                      'flex h-6 w-6 items-center justify-center text-sm rounded-full border transition-colors',
                      // Same four composable states as the week strip: fill for
                      // a day carrying items, a thin primary ring on today, and
                      // the ring drawn OVER the fill when both apply.
                      //
                      // Exactly ONE border-colour and ONE text-colour utility
                      // per cell. This used to stack `border-transparent` (or
                      // the fill's light border) under today's `border-primary`
                      // and `text-text-hero` under the viewed day's
                      // `text-primary-foreground`, trusting the later class in
                      // the list to win. Tailwind does not read the className
                      // order: the winner is whichever utility sits later in
                      // the generated stylesheet, and there `border-transparent`
                      // and `text-text-hero` both come last — so today lost its
                      // ring as soon as the feed scrolled off it, and the viewed
                      // day's number came out dark on the solid green (YAR-420
                      // re-open, round 2). The tests pin the exact token set.
                      //
                      // A past day carries no fill, and the reason is what the
                      // fill MEANS: "there is something here for you to act on".
                      // A day that has gone has nothing left to act on, so the
                      // fill would be an invitation to a day that cannot be
                      // tapped.
                      //
                      // It is NOT that a past day may carry no marker at all.
                      // This comment used to say "past days get nothing at all"
                      // and cite YAR-132 AC #9, which says neither: AC #9 is
                      // "past days are visually disabled, tapping does nothing",
                      // and its own implementation (48a7cf7b) kept the item dots
                      // on past days and suppressed only the SELECTED badge. The
                      // broader wording arrived with the dots-to-fills change in
                      // PR #374, whose body never mentions past days.
                      //
                      // That mis-citation cost real time: YAR-418 (a multi-day
                      // run is invisible on the days it covers) and YAR-385 (a
                      // past day should not show a dot for a completed item) were
                      // read as contradicting each other and both stalled waiting
                      // on a product decision. They do not contradict — a run
                      // that is still going and an item in History are disjoint
                      // by construction (`getOngoingEvents` takes
                      // `end_date >= todayStart`, `getHistoryEvents` takes `<`).
                      // Coverage by a still-running span is carried by the span
                      // bar above the feed, which deliberately does NOT clip at
                      // today (`ongoing.ts` clamps `startCol` to 0), so a past
                      // day inside a running span is already communicated. The
                      // cell says "act here"; the bar says "a period runs
                      // through here". Two channels, on purpose.
                      //
                      // The premise this comment used to give is also gone: the
                      // feed has been floored at today since PR #249, past cells
                      // have no handler, and the `?date=` seed is clamped, so a
                      // past `selectedDate` can no longer occur at all.
                      //
                      // The viewed day — the one the feed is on, which follows
                      // the scroll — is the one solid `surface/primary/default`
                      // cell, today or not (Figma "Mini Calendar / changed
                      // date", both variants). It used to be a bare ring, and
                      // when it was today it was painted `foreground` (#2f2f2f)
                      // instead — the two findings of the YAR-420 re-open.
                      isPast
                        ? 'border-transparent text-disabled-text cursor-default'
                        : isSelected
                          ? 'bg-primary border-transparent text-primary-foreground cursor-pointer'
                          : [
                              'text-text-hero cursor-pointer hover:bg-muted',
                              hasItems ? 'bg-primary-light' : '',
                              // Today's ring is Figma `border/focus` #1f372d (`primary-hover`),
                              // one shade darker than the viewed day's solid #264133 fill.
                              isToday
                                ? 'border-primary-hover'
                                : hasItems
                                  ? 'border-primary-light-border'
                                  : 'border-transparent',
                            ]
                              .filter(Boolean)
                              .join(' '),
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {date.getDate()}
                  </button>
                  {/* The period band. A separate channel from the cell fill on
                    purpose: the fill means "there is something here to act
                    on", and painting a day merely INSIDE a payment window the
                    same way would make the two indistinguishable — which is
                    the reason "just fill every covered day" is not the fix.

                    Square edges where the neighbour is also covered, so a run
                    reads as one continuous band across the row rather than a
                    row of separate pills. Row-aware: the last cell of a week
                    never joins rightwards, because the day to its right is on
                    the line below. */}
                  {isCovered && (
                    <span
                      aria-hidden="true"
                      className={[
                        // Sits in the 12px row gap so the band adds no height
                        // to the 36px row pitch. It bleeds 6px each side into
                        // the column gap so joined neighbours meet.
                        'absolute -bottom-1.5 h-[3px] bg-primary-light-border',
                        joinsLeft ? '-left-1.5' : 'left-0 rounded-l-full',
                        joinsRight ? '-right-1.5' : 'right-0 rounded-r-full',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
